import React, { Component } from 'react';
import { FormattedMessage, injectIntl, InjectedIntl } from 'react-intl';
import { Checkbox, Input, message, Select as AntSelect, Switch } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { SelectValue } from 'antd/lib/select';
import { Link } from 'react-router-dom';
import { ELEMENTS } from '../../../utils';
import {
  IPlayer,
  IVisibility,
  ISlide,
  IElement,
  IPlaylist,
  Status,
} from '../../../redux/modules';
import { Button, Modal, StatusTag } from '../../../components';
import {
  Container,
  ElementsSection,
  EditorWrapper,
  NameSection,
  SectionTitle,
  SectionContent,
  CustomNameInput,
  Divider,
  PlaylistCreateAction,
  PlaylistMembershipActions,
  PlaylistMembershipContent,
  PlaylistMembershipItem,
  PlaylistMembershipList,
  PlaylistMembershipMeta,
  PlaylistMembershipName,
  PlaylistModalControls,
  PlaylistModalField,
  PlaylistModalFooter,
  PlaylistModalLabel,
  PlaylistModalSelectionGrid,
  PlaylistModalSelectionList,
  PlaylistSection,
  ManagedByPlaylistNotice,
  ScheduleSection,
  LocationSection,
  HeaderContainer,
  HeaderRightSide,
  TitleCreative,
} from './styles';

import Actions from './Actions';
import LocationSelector from './LocationSelector';
import ScheduleSelector from './ScheduleSelector';
import { ImageElement, VideoElement, QrElement, TextElement } from './Elements';

const { Option } = AntSelect;

interface ITargetItem {
  id: string;
  title: string;
  subtitle?: string;
}

interface IPlaylistForm {
  _id: string;
  name: string;
  active: boolean;
  allPlayers: boolean;
  players: string[];
  groups: string[];
  items: Array<{
    creativity: string;
    position: number;
  }>;
}

interface IProps {
  creativityId: string;
  updateElementProp?: any;
  updateCreativity: any;
  updateTitle: any;
  updatePlayers: any;
  changeSelection: any;
  visibility?: IVisibility;
  creativityTitle: any;
  intl: InjectedIntl;
  slide: ISlide;
  emergency: any;
  status: Status;
  playersCreativity: IPlayer[];
  players: IPlayer[];
  groups: ITargetItem[];
  playlists: IPlaylist[];
  playlistsContainingCreativity: IPlaylist[];
  addCreativityToPlaylist: (
    playlistId: string,
    creativityId: string
  ) => Promise<IPlaylist | void>;
  removeCreativityFromPlaylist: (
    playlistId: string,
    creativityId: string
  ) => Promise<IPlaylist | void>;
  savePlaylistData: (
    playlist: IPlaylist,
    options?: { navigate?: boolean }
  ) => Promise<IPlaylist | void>;
  hasPublishedEmergency: boolean;
  hasAllPlayersSelected: boolean;
  isPublished?: boolean;
  deleteCreativity: any;
  selected?: boolean | undefined;
  auth?: any;
  handlePlay?: any;
  startDate?: string;
  endDate?: string;
}

interface IState {
  disabledImage: boolean;
  active?: boolean;
  value?: string;
  error?: boolean;
  playlistForm?: IPlaylistForm;
  playlistModalMode?: 'create' | 'edit';
  playlistModalVisible: boolean;
}

class Menu extends Component<IProps, IState> {
  state: IState = {
    disabledImage: false,
    playlistModalVisible: false,
  };

  buildCreatePlaylistForm = (): IPlaylistForm => ({
    _id: `draft_${Date.now()}`,
    name: '',
    active: true,
    allPlayers: true,
    players: [],
    groups: [],
    items: [
      {
        creativity: this.props.creativityId,
        position: 0,
      },
    ],
  });

  buildEditPlaylistForm = (playlist: IPlaylist): IPlaylistForm => ({
    _id: playlist._id,
    name: playlist.name,
    active: playlist.active,
    allPlayers: !!playlist.allPlayers,
    players: playlist.players || [],
    groups: playlist.groups || [],
    items: playlist.items || [],
  });

  getPlaylistTargetSummary = (playlist: IPlaylist) => {
    const { intl } = this.props;

    if (playlist.allPlayers) {
      return intl.formatMessage({ id: 'playlists.allScreens' });
    }

    const parts = [
      playlist.players && playlist.players.length
        ? intl.formatMessage(
            { id: 'playlists.screensCount' },
            { count: playlist.players.length }
          )
        : '',
      playlist.groups && playlist.groups.length
        ? intl.formatMessage(
            { id: 'playlists.groupsCount' },
            { count: playlist.groups.length }
          )
        : '',
    ].filter(Boolean);

    return parts.join(', ') || intl.formatMessage({ id: 'playlists.noTarget' });
  };

  addToPlaylist = async (value: SelectValue) => {
    const { addCreativityToPlaylist, creativityId, playlists } = this.props;

    if (typeof value !== 'string') {
      return;
    }

    const playlist = playlists.find(playlist => playlist._id === value);
    const saved = await addCreativityToPlaylist(value, creativityId);

    if (saved) {
      message.success(
        this.props.intl.formatMessage(
          { id: 'playlists.editor.message.added' },
          {
            playlist: playlist ? playlist.name || playlist._id : 'playlist',
          }
        )
      );
    }
  };

  removeFromPlaylist = (playlist: IPlaylist) => async () => {
    const { creativityId, removeCreativityFromPlaylist } = this.props;
    const saved = await removeCreativityFromPlaylist(
      playlist._id,
      creativityId
    );

    if (saved) {
      message.success(
        this.props.intl.formatMessage(
          { id: 'playlists.editor.message.removed' },
          { playlist: playlist.name || playlist._id }
        )
      );
    }
  };

  openCreatePlaylist = () => {
    this.setState({
      playlistForm: this.buildCreatePlaylistForm(),
      playlistModalMode: 'create',
      playlistModalVisible: true,
    });
  };

  openEditPlaylist = (playlist: IPlaylist) => () => {
    this.setState({
      playlistForm: this.buildEditPlaylistForm(playlist),
      playlistModalMode: 'edit',
      playlistModalVisible: true,
    });
  };

  closePlaylistModal = () => {
    this.setState({
      playlistForm: undefined,
      playlistModalMode: undefined,
      playlistModalVisible: false,
    });
  };

  updatePlaylistForm = (update: Partial<IPlaylistForm>) => {
    const { playlistForm } = this.state;

    if (!playlistForm) {
      return;
    }

    this.setState({
      playlistForm: {
        ...playlistForm,
        ...update,
      },
    });
  };

  updatePlaylistTarget = (
    type: 'players' | 'groups',
    id: string,
    checked: boolean
  ) => {
    const { playlistForm } = this.state;

    if (!playlistForm) {
      return;
    }

    const current = playlistForm[type] || [];
    const next = checked
      ? [...current, id]
      : current.filter(item => item !== id);

    this.updatePlaylistForm({
      [type]: next,
      allPlayers: false,
    } as Partial<IPlaylistForm>);
  };

  savePlaylistForm = async () => {
    const { playlistForm, playlistModalMode } = this.state;

    if (!playlistForm) {
      return;
    }

    if (!playlistForm.name.trim()) {
      message.error(
        this.props.intl.formatMessage({ id: 'playlist.validation.name' })
      );
      return;
    }

    if (
      !playlistForm.allPlayers &&
      !playlistForm.players.length &&
      !playlistForm.groups.length
    ) {
      message.error(
        this.props.intl.formatMessage({ id: 'playlist.validation.target' })
      );
      return;
    }

    const saved = await this.props.savePlaylistData(playlistForm, {
      navigate: false,
    });

    if (saved) {
      message.success(
        this.props.intl.formatMessage({
          id:
            playlistModalMode === 'create'
              ? 'playlists.editor.message.created'
              : 'playlists.editor.message.updated',
        })
      );
      this.closePlaylistModal();
    }
  };

  renderPlaylistMembership = () => {
    const { intl, playlists, playlistsContainingCreativity } = this.props;
    const selectedPlaylistIds = playlistsContainingCreativity.map(
      playlist => playlist._id
    );
    const selectablePlaylists = playlists.filter(
      playlist => !selectedPlaylistIds.includes(playlist._id)
    );

    return (
      <PlaylistSection>
        <SectionTitle>
          <FormattedMessage id="playlists.title" />
        </SectionTitle>
        <PlaylistMembershipList>
          {playlistsContainingCreativity.length ? (
            playlistsContainingCreativity.map(playlist => (
              <PlaylistMembershipItem key={playlist._id}>
                <PlaylistMembershipContent>
                  <PlaylistMembershipName>
                    {playlist.name || playlist._id}
                  </PlaylistMembershipName>
                  <PlaylistMembershipMeta>
                    {intl.formatMessage({
                      id: playlist.active
                        ? 'playlists.active'
                        : 'playlists.inactive',
                    })}
                    {' - '}
                    {this.getPlaylistTargetSummary(playlist)}
                  </PlaylistMembershipMeta>
                </PlaylistMembershipContent>
                <PlaylistMembershipActions>
                  <Button
                    type="whiteBlue"
                    padding={8}
                    button={intl.formatMessage({ id: 'playlists.edit' })}
                    onClick={this.openEditPlaylist(playlist)}
                  />
                  <Link to={`/playlists/${playlist._id}`}>
                    <Button
                      type="whiteBlue"
                      padding={8}
                      button={intl.formatMessage({
                        id: 'playlists.editor.openFullEditor',
                      })}
                    />
                  </Link>
                  <Button
                    type="whiteRed"
                    padding={8}
                    button={intl.formatMessage({ id: 'playlists.remove' })}
                    onClick={this.removeFromPlaylist(playlist)}
                  />
                </PlaylistMembershipActions>
              </PlaylistMembershipItem>
            ))
          ) : (
            <PlaylistMembershipMeta>
              <FormattedMessage id="playlists.editor.empty" />
            </PlaylistMembershipMeta>
          )}
        </PlaylistMembershipList>
        <AntSelect
          style={{ width: '100%', marginTop: 15 }}
          value={undefined}
          onChange={this.addToPlaylist}
          disabled={!selectablePlaylists.length}
          placeholder={intl.formatMessage({
            id: 'playlists.editor.addPlaceholder',
          })}
        >
          {selectablePlaylists.map(playlist => (
            <Option key={playlist._id} value={playlist._id}>
              {playlist.name || playlist._id}
            </Option>
          ))}
        </AntSelect>
        <PlaylistCreateAction>
          <Button
            type="whiteBlue"
            padding={8}
            button={intl.formatMessage({ id: 'playlists.editor.create' })}
            onClick={this.openCreatePlaylist}
          />
        </PlaylistCreateAction>
      </PlaylistSection>
    );
  };

  renderPlaylistModal = () => {
    const { groups, intl, players } = this.props;
    const {
      playlistForm,
      playlistModalMode,
      playlistModalVisible,
    } = this.state;

    if (!playlistForm) {
      return null;
    }

    return (
      <Modal
        visible={playlistModalVisible}
        title={intl.formatMessage({
          id:
            playlistModalMode === 'create'
              ? 'playlists.editor.createTitle'
              : 'playlists.editor.editTitle',
        })}
        handleCustomCancel={this.closePlaylistModal}
        noFooter
      >
        <PlaylistModalField>
          <PlaylistModalLabel>
            <FormattedMessage id="playlists.name" />
          </PlaylistModalLabel>
          <Input
            value={playlistForm.name}
            onChange={event =>
              this.updatePlaylistForm({ name: event.target.value })
            }
          />
        </PlaylistModalField>
        <PlaylistModalControls>
          <Switch
            checked={playlistForm.active}
            onChange={active => this.updatePlaylistForm({ active })}
          />
          <FormattedMessage id="playlists.active" />
        </PlaylistModalControls>
        <PlaylistModalField>
          <Checkbox
            checked={playlistForm.allPlayers}
            onChange={(event: CheckboxChangeEvent) =>
              this.updatePlaylistForm({
                allPlayers: event.target.checked,
                ...(event.target.checked ? { players: [], groups: [] } : {}),
              })
            }
          >
            <FormattedMessage id="playlists.allScreens" />
          </Checkbox>
        </PlaylistModalField>
        <PlaylistModalSelectionGrid>
          <PlaylistModalSelectionList>
            <PlaylistModalLabel>
              <FormattedMessage id="playlists.groups" />
            </PlaylistModalLabel>
            {groups.map(group => (
              <div key={group.id}>
                <Checkbox
                  disabled={playlistForm.allPlayers}
                  checked={playlistForm.groups.includes(group.id)}
                  onChange={(event: CheckboxChangeEvent) =>
                    this.updatePlaylistTarget(
                      'groups',
                      group.id,
                      event.target.checked
                    )
                  }
                >
                  {group.title}
                </Checkbox>
              </div>
            ))}
          </PlaylistModalSelectionList>
          <PlaylistModalSelectionList>
            <PlaylistModalLabel>
              <FormattedMessage id="playlists.screens" />
            </PlaylistModalLabel>
            {players.map(player => (
              <div key={player._id}>
                <Checkbox
                  disabled={playlistForm.allPlayers}
                  checked={playlistForm.players.includes(player._id)}
                  onChange={(event: CheckboxChangeEvent) =>
                    this.updatePlaylistTarget(
                      'players',
                      player._id,
                      event.target.checked
                    )
                  }
                >
                  {player.playerName || player.name}
                </Checkbox>
              </div>
            ))}
          </PlaylistModalSelectionList>
        </PlaylistModalSelectionGrid>
        <PlaylistModalFooter>
          {playlistModalMode === 'edit' && (
            <Link to={`/playlists/${playlistForm._id}`}>
              <Button
                type="whiteBlue"
                button={intl.formatMessage({
                  id: 'playlists.editor.openFullEditor',
                })}
              />
            </Link>
          )}
          <Button
            type="whiteBlue"
            button={intl.formatMessage({ id: 'actions.modalCancel' })}
            onClick={this.closePlaylistModal}
          />
          <Button
            button={intl.formatMessage({ id: 'playlists.save' })}
            onClick={this.savePlaylistForm}
          />
        </PlaylistModalFooter>
      </Modal>
    );
  };

  renderDirectProgramming = () => {
    const {
      creativityId,
      emergency,
      hasAllPlayersSelected,
      playlistsContainingCreativity,
      status,
      endDate,
      startDate,
    } = this.props;
    const isPlaylistManaged =
      !emergency && playlistsContainingCreativity.length > 0;

    if (isPlaylistManaged) {
      return (
        <>
          <ScheduleSection>
            <SectionTitle>
              <FormattedMessage id="editor.info.screen.titleActivity" />
            </SectionTitle>
            <ManagedByPlaylistNotice>
              <FormattedMessage id="playlists.editor.managedNotice" />
            </ManagedByPlaylistNotice>
          </ScheduleSection>
          <Divider />
        </>
      );
    }

    return (
      <>
        <ScheduleSection>
          <SectionTitle>
            <FormattedMessage id="editor.info.screen.titleActivity" />
          </SectionTitle>
          <SectionContent>
            {!emergency && (
              <ScheduleSelector
                visibility={this.props.visibility}
                creativityId={creativityId}
                status={status}
                startDate={startDate}
                endDate={endDate}
              />
            )}
          </SectionContent>
        </ScheduleSection>

        <Divider />

        <LocationSection>
          <LocationSelector
            id={creativityId}
            status={status}
            hasAllPlayersSelected={hasAllPlayersSelected}
          />
        </LocationSection>

        <Divider />
      </>
    );
  };

  renderElement = (element: IElement, index: number) => {
    const { creativityId, status } = this.props;
    if (element.locked) {
      return null;
    }

    switch (element.type) {
      case ELEMENTS.TEXT:
        return (
          <TextElement
            key={index}
            index={index}
            creativeId={creativityId}
            element={element}
            status={status}
          />
        );
      case ELEMENTS.IMAGE:
        return (
          <ImageElement
            key={index}
            creativeId={creativityId}
            element={element}
            index={index}
            status={status}
          />
        );
      case ELEMENTS.QR:
        return (
          <QrElement
            key={index}
            index={index}
            creativeId={creativityId}
            element={element}
            status={status}
          />
        );
      case ELEMENTS.VIDEO:
        return (
          <VideoElement
            key={index}
            creativeId={creativityId}
            element={element}
            index={index}
            status={status}
          />
        );
      case ELEMENTS.WEATHER:
        return null;
      default:
        return null;
    }
  };

  render() {
    const {
      creativityId,
      creativityTitle,
      intl,
      slide,
      emergency,
      status,
      selected,
      hasAllPlayersSelected,
      endDate,
      startDate,
    } = this.props;
    return (
      <Container>
        {this.renderPlaylistModal()}
        <HeaderContainer>
          <TitleCreative>{creativityTitle}</TitleCreative>
          <HeaderRightSide>
            <Button
              onClick={this.props.handlePlay()}
              type="preview"
              button={intl!.formatMessage({
                id: 'actions.buttons.play',
              })}
              padding={8}
              icon="play-circle"
            />
            {!emergency && (
              <div>
                <StatusTag
                  status={status}
                  endDate={endDate}
                  startDate={startDate}
                />
              </div>
            )}
          </HeaderRightSide>
        </HeaderContainer>
        <EditorWrapper>
          <form>
            {!emergency && (
              <NameSection>
                <SectionTitle>
                  <FormattedMessage id={'editor.info.field.creativity'} />
                </SectionTitle>
                <SectionContent>
                  <CustomNameInput
                    disabled={
                      status === Status.PUBLISHED ||
                      status === Status.PENDING_VALIDATION
                    }
                    value={creativityTitle || ''}
                    placeholder={'editor.info.placeholer.creativity'}
                    onChange={event =>
                      this.props.updateTitle(
                        this.props.creativityId,
                        'creativity',
                        event.target.value
                      )
                    }
                  />
                </SectionContent>
              </NameSection>
            )}
            <Divider />

            {!emergency && this.renderPlaylistMembership()}

            <Divider />

            {this.renderDirectProgramming()}

            <ElementsSection>
              <SectionTitle style={{ marginBottom: '10px' }}>
                <FormattedMessage id={'editor.info.elements.sectionTitle'} />
              </SectionTitle>
              {slide &&
                Array.isArray(slide.elements) &&
                slide.elements.map((item, i) => this.renderElement(item, i))}
            </ElementsSection>

            <Divider />

            <Actions
              id={creativityId}
              isEmergency={emergency}
              status={status}
              selected={selected}
              endDate={endDate}
            />
          </form>
        </EditorWrapper>
      </Container>
    );
  }
}

export default injectIntl(Menu);
